import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let siteKeyRequest = null;
let scriptRequest = null;

// The Turnstile site key comes from the backend .env (empty while the check is off), fetched once
export function useTurnstileSiteKey() {
  const [siteKey, setSiteKey] = useState('');
  useEffect(() => {
    siteKeyRequest = siteKeyRequest || axios.get(`${API}/auth/captcha-config`)
      .then((res) => res.data?.turnstile_site_key || '')
      .catch(() => '');
    let alive = true;
    siteKeyRequest.then((key) => { if (alive) setSiteKey(key); });
    return () => { alive = false; };
  }, []);
  return siteKey;
}

function loadScript() {
  scriptRequest = scriptRequest || new Promise((resolve, reject) => {
    if (window.turnstile) return resolve(window.turnstile);
    const script = Object.assign(document.createElement('script'), { src: SCRIPT_URL, async: true, defer: true });
    script.onload = () => resolve(window.turnstile);
    script.onerror = () => { scriptRequest = null; reject(new Error('Turnstile did not load')); };
    document.head.appendChild(script);
  });
  return scriptRequest;
}

// Cloudflare's "are you a person" check. Usually passes on its own without a click. Renders
// nothing when the site has no key. A token works once: change `resetKey` to get a fresh one.
export default function Turnstile({ siteKey, onToken, resetKey = 0 }) {
  const box = useRef(null);
  const widget = useRef(null);
  const tokenCallback = useRef(onToken);
  tokenCallback.current = onToken;

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;
    loadScript().then((turnstile) => {
      if (cancelled || !box.current || !turnstile) return;
      widget.current = turnstile.render(box.current, {
        sitekey: siteKey,
        theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
        callback: (token) => tokenCallback.current(token),
        'expired-callback': () => tokenCallback.current(''),
        'error-callback': () => tokenCallback.current(''),
      });
    }).catch(() => tokenCallback.current(''));
    return () => {
      cancelled = true;
      if (widget.current && window.turnstile) window.turnstile.remove(widget.current);
      widget.current = null;
    };
  }, [siteKey, resetKey]);

  if (!siteKey) return null;
  return <div ref={box} className="flex min-h-[65px] justify-center" data-testid="turnstile" />;
}
